import { Component } from '@theme/component';
import { isClickedOutside, onAnimationEnd } from '@theme/utilities';

export class VideoCallScheduler extends Component {
  requiredRefs = ['dialog', 'form', 'dateInput', 'timeSlotGrid', 'step1', 'step2', 'stepSuccess', 'selectedDateTimeText'];
  
  #previousScrollY = 0;
  selectedTime = null;

  connectedCallback() {
    super.connectedCallback();
    
    if (this.refs.form) {
      this.refs.form.addEventListener('submit', this.handleSubmit.bind(this));
    }
    
    // Set min date of date picker to today
    const dateInput = this.refs.dateInput;
    if (dateInput) {
      const today = new Date().toISOString().split('T')[0];
      dateInput.min = today;
      dateInput.value = today;
      this.updateSlots();
    }
  }

  showDialog() {
    const { dialog } = this.refs;
    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  closeDialog = async () => {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    dialog.style.animation = 'none';
    void dialog.offsetWidth; // Force a reflow
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');
    
    // Reset back to first step
    this.resetForm();
  };

  #handleClick = (event) => {
    const { dialog } = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.closeDialog();
  };

  updateSlots() {
    const dateInput = this.refs.dateInput;
    if (!dateInput || !dateInput.value) return;

    const dateValue = dateInput.value;

    // Retrieve comma-separated slots from attribute
    const slotsAttr = this.getAttribute('data-slots') || '10:00 AM, 11:00 AM, 12:00 PM, 2:00 PM, 3:00 PM, 4:00 PM';
    const slots = slotsAttr.split(',').map(s => s.trim()).filter(Boolean);
    
    const grid = this.refs.timeSlotGrid;
    if (!grid) return;
    grid.innerHTML = '';

    if (slots.length === 0) {
      grid.innerHTML = '<p class="video-call-no-slots">No slots available.</p>';
      return;
    }

    // Check if the selected date is today to filter out past times
    const isToday = new Date(dateValue).toDateString() === new Date().toDateString();
    let availableSlotsCount = 0;

    slots.forEach(slot => {
      if (isToday) {
        const parsedTime = this.#parseSlotTime(slot);
        if (parsedTime) {
          const slotTime = new Date();
          slotTime.setHours(parsedTime.hours, parsedTime.minutes, 0, 0);
          if (slotTime < new Date()) {
            // Time is in the past, skip or disable it
            return;
          }
        }
      }

      availableSlotsCount++;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'video-call-slot-btn';
      button.textContent = slot;
      button.setAttribute('aria-label', `Select time slot ${slot}`);
      button.addEventListener('click', () => this.selectSlot(button, slot));
      grid.appendChild(button);
    });

    if (availableSlotsCount === 0) {
      grid.innerHTML = '<p class="video-call-no-slots">No slots available for today.</p>';
    }
  }

  #parseSlotTime(slot) {
    try {
      const parts = slot.trim().split(/\s+/);
      if (parts.length < 1) return null;
      
      const timePart = parts[0];
      const modifier = parts[1] ? parts[1].toUpperCase() : '';
      
      let [hoursStr, minutesStr] = timePart.split(':');
      let hours = Number(hoursStr);
      let minutes = minutesStr ? Number(minutesStr) : 0;
      
      if (modifier === 'PM' && hours < 12) hours += 12;
      if (modifier === 'AM' && hours === 12) hours = 0;
      
      return { hours, minutes };
    } catch (e) {
      return null;
    }
  }

  selectSlot(button, slotValue) {
    // Unhighlight others
    const buttons = this.refs.timeSlotGrid.querySelectorAll('.video-call-slot-btn');
    buttons.forEach(btn => btn.classList.remove('selected'));

    button.classList.add('selected');
    this.selectedTime = slotValue;

    // Move to step 2 automatically with a nice transition
    setTimeout(() => {
      this.goToStep2();
    }, 250);
  }

  goToStep2() {
    if (!this.selectedTime) return;

    const dateVal = this.refs.dateInput.value;
    const formattedDate = new Date(dateVal).toLocaleDateString(undefined, { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });

    if (this.refs.selectedDateTimeText) {
      this.refs.selectedDateTimeText.textContent = `${formattedDate} at ${this.selectedTime}`;
    }

    if (this.refs.step1 && this.refs.step2) {
      this.refs.step1.style.display = 'none';
      this.refs.step2.style.display = 'block';
    }
  }

  goToStep1() {
    if (this.refs.step1 && this.refs.step2) {
      this.refs.step2.style.display = 'none';
      this.refs.step1.style.display = 'block';
    }
  }

  resetForm() {
    this.selectedTime = null;
    if (this.refs.form) {
      this.refs.form.reset();
    }
    
    if (this.refs.timeSlotGrid) {
      const buttons = this.refs.timeSlotGrid.querySelectorAll('.video-call-slot-btn');
      buttons.forEach(btn => btn.classList.remove('selected'));
    }
    
    if (this.refs.stepSuccess && this.refs.step2 && this.refs.step1) {
      this.refs.stepSuccess.style.display = 'none';
      this.refs.step2.style.display = 'none';
      this.refs.step1.style.display = 'block';
    }

    // Set date back to today
    const today = new Date().toISOString().split('T')[0];
    if (this.refs.dateInput) {
      this.refs.dateInput.value = today;
      this.updateSlots();
    }
  }

  async handleSubmit(event) {
    event.preventDefault();
    const form = this.refs.form;
    if (!form) return;

    const submitBtn = form.querySelector('button[type="submit"]');
    if (submitBtn) {
      submitBtn.disabled = true;
      const span = submitBtn.querySelector('span');
      if (span) span.textContent = 'Submitting...';
    }

    // Populate hidden fields
    const hiddenDateInput = form.querySelector('.hiddenDateInput');
    const hiddenTimeInput = form.querySelector('.hiddenTimeInput');
    
    let formattedDate = '';
    if (hiddenDateInput && this.refs.dateInput) {
      const dateVal = this.refs.dateInput.value;
      formattedDate = new Date(dateVal).toLocaleDateString(undefined, { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
      hiddenDateInput.value = formattedDate;
    }
    if (hiddenTimeInput) {
      hiddenTimeInput.value = this.selectedTime;
    }

    const formData = new FormData(form);
    const object = Object.fromEntries(formData);
    const json = JSON.stringify(object);

    try {
      await fetch("https://api.web3forms.com/submit", {
          method: "POST",
          headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
          },
          body: json
      });
    } catch (err) {
      console.error('Failed to post booking data:', err);
    }

    // Transition to success screen
    if (this.refs.step2 && this.refs.stepSuccess) {
      this.refs.step2.style.display = 'none';
      this.refs.stepSuccess.style.display = 'flex';

      const successDateTime = this.refs.stepSuccess.querySelector('.success-booking-time');
      if (successDateTime) {
        successDateTime.textContent = `${formattedDate} at ${this.selectedTime}`;
      }
    }

    if (submitBtn) {
      submitBtn.disabled = false;
      const span = submitBtn.querySelector('span');
      if (span) span.textContent = 'Confirm Appointment';
    }
  }
}

if (!customElements.get('video-call-scheduler')) {
  customElements.define('video-call-scheduler', VideoCallScheduler);
}
